import { initContract } from '@ts-rest/core'
import {
    ApplySuggestionRequestSchema,
    ApplySuggestionResultSchema,
    GetSuggestionPreviewQuerySchema,
    GetSuggestionTreeResponseSchema,
    GetSuggestionsQuerySchema,
    SuggestionIdParamsSchema,
    SuggestionItemSchema,
} from '../schemas/suggestions'
import { defaultErrorResponses } from './errors'

const c = initContract()

/**
 * Label Suggestions Contract - Hierarchical bulk labeling with parent/child suggestions.
 * Auto-generates parent suggestions when multiple categories exist.
 * Supports customizable actions: apply_label, mark_read, archive, move_to_label, delete.
 * All endpoints require authMiddleware and log to activity_logs on write operations.
 */
export const suggestionsContract = c.router({
  /**
   * Returns hierarchical parent/child suggestions for bulk label application with auto-generated parents.
   * Gets: limit (default: 20, max: 100), offset from query.
   * Returns: { rootId (parent id or null), items[] with id, parent_id, kind ('parent'|'category'|'custom'), title, affected_email_count, preview_emails, actions[] }.
   * Side Effects: None (read-only). Auto-generates parent when multiple categories exist.
   */
  list: {
    method: 'GET',
    path: '/v1/manage/suggestions',
    query: GetSuggestionsQuerySchema,
    responses: {
      200: GetSuggestionTreeResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List hierarchical label suggestions',
      description: 'Returns hierarchical parent/child suggestions with auto-generated parents for multiple categories.',
    },
  },
  /**
   * Fetches paginated email previews and default actions for a specific suggestion before user confirms.
   * Gets: suggestionId path param, limit/offset from query.
   * Returns: SuggestionItem with preview_emails[], actions[] array (ordered: apply_label, mark_read, archive, etc.).
   * Side Effects: None (read-only).
   */
  preview: {
    method: 'GET',
    path: '/v1/manage/suggestions/:suggestionId/preview',
    pathParams: SuggestionIdParamsSchema,
    query: GetSuggestionPreviewQuerySchema,
    responses: {
      200: SuggestionItemSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Preview emails for a suggestion',
      description: 'Get paginated email previews and default actions for a suggestion before applying.',
    },
  },
  /**
   * Executes suggestion actions (apply labels, archive, mark read) on emails and logs activity.
   * Gets: suggestionId path param, childIds[], emailIds[], actions[] overrides in body.
   * Returns: { success: true, operation_id, affected_count }.
   * Side Effects: Updates Gmail (archive=remove INBOX, labels=add label, mark_read=remove UNREAD), creates activity_log entry, batches by 500 messages.
   */
  apply: {
    method: 'POST',
    path: '/v1/manage/suggestions/:suggestionId/apply',
    pathParams: SuggestionIdParamsSchema,
    body: ApplySuggestionRequestSchema,
    responses: {
      200: ApplySuggestionResultSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Apply suggestion actions',
      description: 'Execute suggestion actions on selected emails with optional overrides. Logs to activity_logs.',
    },
  },
})
